/**
 * Position interpolation utilities for smooth vehicle animation.
 */

import { RENDER_CONFIG } from '../config';

/**
 * Ease-out cubic function for smooth deceleration.
 */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Linear interpolation between two values.
 */
export function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * t;
}

/**
 * Interpolate position between current and target coordinates.
 *
 * @param currentLat - Current latitude
 * @param currentLon - Current longitude
 * @param targetLat - Target latitude
 * @param targetLon - Target longitude
 * @param lastUpdateTime - Timestamp of last position update
 * @param now - Current timestamp (defaults to Date.now())
 * @param duration - Interpolation duration in ms (defaults to config)
 * @returns Interpolated [latitude, longitude]
 */
export function interpolatePosition(
  currentLat: number,
  currentLon: number,
  targetLat: number,
  targetLon: number,
  lastUpdateTime: number,
  now: number = Date.now(),
  duration: number = RENDER_CONFIG.INTERPOLATION_DURATION_MS
): [number, number] {
  const elapsed = now - lastUpdateTime;
  const t = Math.min(elapsed / duration, 1.0);
  // Linear interpolation for constant velocity animation
  // (matches Västtrafik Android app behavior)

  return [
    lerp(currentLat, targetLat, t),
    lerp(currentLon, targetLon, t),
  ];
}

/**
 * Interpolate heading angle, handling wrap-around at 360 degrees.
 *
 * @param currentHeading - Current heading in degrees
 * @param targetHeading - Target heading in degrees
 * @param t - Interpolation factor (0-1)
 * @returns Interpolated heading in degrees
 */
export function interpolateHeading(
  currentHeading: number | null,
  targetHeading: number | null,
  t: number
): number | null {
  if (currentHeading === null || targetHeading === null) {
    return targetHeading;
  }

  // Normalize angles to 0-360 range
  const current = ((currentHeading % 360) + 360) % 360;
  const target = ((targetHeading % 360) + 360) % 360;

  // Calculate shortest path
  let diff = target - current;
  if (diff > 180) {
    diff -= 360;
  } else if (diff < -180) {
    diff += 360;
  }

  const result = current + diff * t;
  return ((result % 360) + 360) % 360;
}

/**
 * Check if interpolation is complete.
 *
 * @param lastUpdateTime - Timestamp of last position update
 * @param now - Current timestamp
 * @param duration - Interpolation duration in ms
 * @returns true if interpolation is complete
 */
export function isInterpolationComplete(
  lastUpdateTime: number,
  now: number = Date.now(),
  duration: number = RENDER_CONFIG.INTERPOLATION_DURATION_MS
): boolean {
  return now - lastUpdateTime >= duration;
}
